import { useState, useEffect, useContext } from "react";
import { DATA_CATEGORIES } from "./DataCategories";
import { Box, Typography } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import API from "../api2";
import ControlPanel from "./FilterTool/ControlPanel";
import AnalyticsGraphBase from "./Graph/AnalyticsGraphBase";
import { AnalyticsData, SelectedMetrics, StorageLocation } from "../Types/AnalyticsGraph";
import BenchmarkSettings from "../Types/BenchmarkSettings";
import { processBenchmarkData, hasBenchmarkData } from "./Benchmark";
import { ErrorDisplay } from "./Graph/GraphHelpers/ChartStateDisplays";
import UserContext from "../User/UserSettings/UserContext";
import useNotification from "../Hooks/useNotification";

interface DataPointService {
    serviceType: string;
    dateServiced: string;
    id: number;
}

interface ServiceData {
    id: string;
    label: string;
    type: string;
    unit: string;
    data: DataPointService[];
    metadata: any;
}

function AnalyticsBase() {
    const { user } = useContext(UserContext) || { user: { mode: 'dark' } };
    const mode = user?.mode || 'dark';
    const isMobile = user?.mobile || false;
    const [selectedMetrics, setSelectedMetrics] = useState<SelectedMetrics>({ agristarMetrics: [], serviceMetrics: [] });
    const [data, setData] = useState<AnalyticsData | null>(null);
    const [serviceData, setServiceData] = useState<ServiceData | null>(null);
    const [selectedStorage, setSelectedStorage] = useState<StorageLocation>({ id: 0, locationName: '' });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const notify = useNotification();
    const [benchmarkSettings, setBenchmarkSettings] = useState<BenchmarkSettings>({
        comparisonTargetType: 'none',
        comparisonTargetValue: null,
        averagingPeriodDays: 0,
    });
    // Add benchmark-related state
    const [benchmarkData, setBenchmarkData] = useState<any>(null);

    useEffect(() => {
        setData(null);
        setServiceData(null);
        setBenchmarkData(null);
    }, []);

    // Reset benchmark settings when storage selection changes
    useEffect(() => {
        if (selectedStorage.id && benchmarkSettings.comparisonTargetType === 'ownStorage') {
            // If benchmark is set to compare to another storage and the main storage changes,
            // reset benchmark settings to prevent invalid comparisons
            setBenchmarkSettings({
                comparisonTargetType: 'none',
                comparisonTargetValue: null,
                averagingPeriodDays: 0,
            });
            setBenchmarkData(null);
            console.log('Benchmark settings reset due to storage selection change');
        }
    }, [selectedStorage.id]);

    const getMetricInfo = (metricKey: string) => {
        for (const category of Object.values(DATA_CATEGORIES)) {
            const option = category.options.find(opt => opt.key === metricKey);
            if (option) {
                return { ...option, color: category.color, unit: category.unit };
            }
        }
        return { key: metricKey, label: metricKey, color: "#666", unit: "" };
    };

    const submitDataFetch = async function (timeData: object, selectedMetrics: SelectedMetrics, selectedStorage: StorageLocation,) {
        try {
            setLoading(true);
            setError(null);

            const res = await API.getAnalyticsData({ timeData, selectedMetrics, selectedStorage, benchmarkSettings });

            if (res.data) {
                setData(res.data);
                console.log("Finished fetching data", res.data);
                if (res.message) {
                    notify(res.message, 'warning');
                }
            } else {
                if (res.message) setError(res.message);
                else setError("No data received from server, please select a valid storage location and metrics.");
            }

            if (res.serviceData) {
                setServiceData(res.serviceData);
            } else {
                setServiceData(null);
            }
            console.log(res.data)
            console.log(res.benchmark)
            // Handle benchmark data
            if (res.benchmark) {
                const processedBenchmark = processBenchmarkData(res.benchmark);
                setBenchmarkData(processedBenchmark);
                console.log("Benchmark data received:", processedBenchmark);
            } else {
                setBenchmarkData(null);
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError("Failed to fetch data");
            setBenchmarkData(null);
        } finally {
            setLoading(false);
        }
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{
                p: { xs: 1, sm: 2, md: 3 },
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                    Analytics Dashboard
                </Typography>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorDisplay error={error} />
                    </Box>
                )}

                {/* Helpful guidance when no data is displayed */}
                {!loading && !data && !error && selectedMetrics.agristarMetrics.length === 0 && (
                    <Box sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: 'info.light',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'info.main'
                    }}>
                        <Typography variant="body2" color="info.contrastText">
                            <strong>Get Started:</strong> Use the control panel below to select a storage location, choose metrics, and set your date range to view analytics data.
                        </Typography>
                    </Box>
                )}

                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flex: 1 }}>
                        <AnalyticsGraphBase
                            getMetricInfo={getMetricInfo}
                            selectedMetrics={selectedMetrics}
                            data={data}
                            serviceData={serviceData}
                            loading={loading}
                            error={error}
                            submitDataFetch={submitDataFetch}
                            selectedStorage={selectedStorage}
                            benchmarkData={benchmarkData}
                            mode={mode}
                        />
                        <ControlPanel
                            isMobile={isMobile}
                            selectedMetrics={selectedMetrics}
                            setSelectedMetrics={setSelectedMetrics}
                            getMetricInfo={getMetricInfo}
                            submitDataFetch={submitDataFetch}
                            selectedStorage={selectedStorage}
                            setSelectedStorage={setSelectedStorage}
                            benchmarkSettings={benchmarkSettings}
                            setBenchmarkSettings={setBenchmarkSettings}
                        />
                    </Box>
                </Box>
            </Box>
        </LocalizationProvider>
    );
}

export default AnalyticsBase;